export type SentryEnvironment = 'development' | 'staging' | 'production';

type PublicEnv = Partial<Record<string, string | undefined>>;

const LOCAL_HOST_PATTERN = /^(localhost|127(?:\.\d{1,3}){3}|0\.0\.0\.0)$/i;
const DEV_HOST_PATTERN = /(^|[.-])dev(?=[.-]|$)/i;
const STAGING_HOST_PATTERN = /(^|[.-])(st|stage|staging)(?=[.-]|$)/i;

const getHostname = (value: string | undefined): string | undefined => {
  if (!value) return undefined;

  try {
    return new URL(value).hostname;
  } catch {
    const withoutProtocol = value.replace(/^[a-z]+:\/\//i, '');
    return withoutProtocol.split('/')[0]?.split(':')[0];
  }
};

const getSentryHostname = (env: PublicEnv): string | undefined =>
  getHostname(env.NEXT_PUBLIC_DOMAIN) ??
  getHostname(env.NEXT_PUBLIC_API_DOMAIN);

//* NEXT_PUBLIC_* 인라인은 `process.env.NEXT_PUBLIC_X` 정적 접근에만 적용된다.
//* process.env 객체를 통째로 넘겨 동적으로 읽으면 브라우저 번들은 빈 process 폴리필을 읽어
//* 항상 undefined → 게이트가 모든 환경에서 false → Sentry.init 미실행.
const INLINED_PUBLIC_ENV: PublicEnv = {
  NEXT_PUBLIC_DOMAIN: process.env.NEXT_PUBLIC_DOMAIN,
  NEXT_PUBLIC_API_DOMAIN: process.env.NEXT_PUBLIC_API_DOMAIN,
};

export const getSentryEnvironment = (
  env: PublicEnv = INLINED_PUBLIC_ENV,
): SentryEnvironment | undefined => {
  const hostname = getSentryHostname(env);

  if (!hostname) return undefined;
  if (LOCAL_HOST_PATTERN.test(hostname) || DEV_HOST_PATTERN.test(hostname)) {
    return 'development';
  }
  if (STAGING_HOST_PATTERN.test(hostname)) {
    return 'staging';
  }

  return 'production';
};

export const isSentryEnabled = (env: PublicEnv = INLINED_PUBLIC_ENV): boolean =>
  getSentryEnvironment(env) === 'production';
